import { useState, useEffect } from "react";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { 
  BarChart3, Users, TrendingUp, TrendingDown, Award, AlertTriangle,
  RefreshCw, Lock, Unlock, Eye, Calculator, Building2, Send, CheckCircle,
  XCircle, RotateCcw, Clock, ClipboardCheck, Plus, X, FileText
} from "lucide-react";

export default function PerformanceSummary({ user }) {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Period selection
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  
  // Data
  const [dashboard, setDashboard] = useState(null);
  const [summaries, setSummaries] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  
  // Detail view
  const [detailDialog, setDetailDialog] = useState({ open: false, summary: null });
  
  // Submit for Review Dialog
  const [submitDialog, setSubmitDialog] = useState({ open: false, summary: null });
  const [submitting, setSubmitting] = useState(false);
  const [submitForm, setSubmitForm] = useState({
    review_comments: "",
    strengths: [],
    areas_for_improvement: [],
    recommendations: ""
  });
  const [newStrength, setNewStrength] = useState("");
  const [newImprovement, setNewImprovement] = useState("");
  
  // HR Review Dialog
  const [reviewDialog, setReviewDialog] = useState({ open: false, review: null });
  const [hrComments, setHrComments] = useState("");
  const [processing, setProcessing] = useState(false);
  
  const months = [
    { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
    { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
    { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
    { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" }
  ];
  
  const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - 2 + i);
  
  // Check if user is HR (can approve reviews)
  const isHR = user?.department === "Human Resources" || user?.access_level === "Admin";

  useEffect(() => {
    fetchDepartments();
    fetchPendingCount();
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchSummaries();
    if (isHR) {
      fetchPendingReviews();
    }
  }, [selectedYear, selectedMonth, selectedDepartment]);

  const fetchDepartments = async () => {
    try {
      const res = await api.get('/employee-kra-assignments?include_history=true');
      const deptSet = new Set();
      res.data.forEach(a => {
        if (a.department_id) {
          deptSet.add(a.department_id);
        }
      });
      setDepartments(Array.from(deptSet).map(d => ({ id: d, name: d })));
    } catch (error) {
      console.error("Failed to load departments");
    }
  };

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      let url = `/performance/dashboard?period_year=${selectedYear}&period_month=${selectedMonth}`;
      if (selectedDepartment && selectedDepartment !== "all") {
        url += `&department_id=${selectedDepartment}`;
      }
      const res = await api.get(url);
      setDashboard(res.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchSummaries = async () => {
    try {
      let url = `/performance/summaries?period_year=${selectedYear}&period_month=${selectedMonth}`;
      if (selectedDepartment && selectedDepartment !== "all") {
        url += `&department_id=${selectedDepartment}`;
      }
      const res = await api.get(url);
      setSummaries(res.data);
    } catch (error) {
      toast.error("Failed to load summaries");
    }
  };
  
  const fetchPendingReviews = async () => {
    try {
      const res = await api.get(`/performance/reviews?status=submitted&period_year=${selectedYear}&period_month=${selectedMonth}`);
      setPendingReviews(res.data);
    } catch (error) {
      console.error("Failed to load pending reviews");
    }
  };
  
  const fetchPendingCount = async () => {
    try {
      const res = await api.get('/performance/reviews/pending-count');
      setPendingCount(res.data.pending_count || 0);
    } catch (error) {
      console.error("Failed to load pending count");
    }
  };

  const handleGenerateAll = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/performance/summaries/generate-bulk', {
        period_year: selectedYear,
        period_month: selectedMonth,
        department_id: selectedDepartment !== "all" ? selectedDepartment : null
      });
      toast.success(`Generated ${res.data.generated} summaries`);
      if (res.data.skipped_locked > 0) {
        toast.info(`${res.data.skipped_locked} locked summaries skipped`);
      }
      fetchDashboard();
      fetchSummaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate summaries");
    } finally {
      setGenerating(false);
    }
  };

  const handleLockToggle = async (summaryId, isLocked) => {
    try {
      const endpoint = isLocked ? 'unlock' : 'lock';
      await api.post(`/performance/summaries/${summaryId}/${endpoint}`);
      toast.success(`Summary ${isLocked ? 'unlocked' : 'locked'} successfully`);
      fetchSummaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update lock status");
    }
  };

  const openDetailDialog = async (summaryId) => {
    try {
      const res = await api.get(`/performance/summaries/${summaryId}`);
      setDetailDialog({ open: true, summary: res.data });
    } catch (error) {
      toast.error("Failed to load summary details");
    }
  };
  
  // Submit for Review Functions
  const openSubmitDialog = (summary) => {
    setSubmitForm({
      review_comments: "",
      strengths: [],
      areas_for_improvement: [],
      recommendations: ""
    });
    setNewStrength("");
    setNewImprovement("");
    setSubmitDialog({ open: true, summary });
  };
  
  const addStrength = () => {
    if (newStrength.trim()) {
      setSubmitForm(prev => ({
        ...prev,
        strengths: [...prev.strengths, newStrength.trim()]
      }));
      setNewStrength("");
    }
  };
  
  const removeStrength = (index) => {
    setSubmitForm(prev => ({
      ...prev,
      strengths: prev.strengths.filter((_, i) => i !== index)
    }));
  };
  
  const addImprovement = () => {
    if (newImprovement.trim()) {
      setSubmitForm(prev => ({
        ...prev,
        areas_for_improvement: [...prev.areas_for_improvement, newImprovement.trim()]
      }));
      setNewImprovement("");
    }
  };
  
  const removeImprovement = (index) => {
    setSubmitForm(prev => ({
      ...prev,
      areas_for_improvement: prev.areas_for_improvement.filter((_, i) => i !== index)
    }));
  };
  
  const handleSubmitForReview = async () => {
    if (!submitDialog.summary) return;
    
    setSubmitting(true);
    try {
      await api.post(`/performance/summaries/${submitDialog.summary.id}/submit-for-review`, submitForm);
      toast.success("Performance review submitted successfully");
      setSubmitDialog({ open: false, summary: null });
      fetchSummaries();
      fetchPendingCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit for review");
    } finally {
      setSubmitting(false);
    }
  };
  
  // HR Review Functions
  const openReviewDialog = async (review) => {
    setHrComments("");
    try {
      const res = await api.get(`/performance/reviews/${review.id}`);
      setReviewDialog({ open: true, review: res.data });
    } catch (error) {
      toast.error("Failed to load review details");
    }
  };
  
  const handleApprove = async () => {
    if (!reviewDialog.review) return;
    
    setProcessing(true);
    try {
      await api.post(`/performance/reviews/${reviewDialog.review.id}/approve`, { hr_comments: hrComments });
      toast.success("Review approved successfully");
      setReviewDialog({ open: false, review: null });
      fetchSummaries();
      fetchPendingReviews();
      fetchPendingCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve review");
    } finally {
      setProcessing(false);
    }
  };
  
  const handleReject = async () => {
    if (!reviewDialog.review || !hrComments.trim()) {
      toast.error("Please provide comments for rejection");
      return;
    }
    
    setProcessing(true);
    try {
      await api.post(`/performance/reviews/${reviewDialog.review.id}/reject`, { hr_comments: hrComments });
      toast.success("Review rejected");
      setReviewDialog({ open: false, review: null });
      fetchSummaries();
      fetchPendingReviews();
      fetchPendingCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject review");
    } finally {
      setProcessing(false);
    }
  };
  
  const handleRequestRevision = async () => {
    if (!reviewDialog.review || !hrComments.trim()) {
      toast.error("Please provide comments for revision request");
      return;
    }
    
    setProcessing(true);
    try {
      await api.post(`/performance/reviews/${reviewDialog.review.id}/request-revision`, { hr_comments: hrComments });
      toast.success("Revision requested");
      setReviewDialog({ open: false, review: null });
      fetchSummaries();
      fetchPendingReviews();
      fetchPendingCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request revision");
    } finally {
      setProcessing(false);
    }
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A+': return 'bg-emerald-500 text-white';
      case 'A': return 'bg-green-500 text-white';
      case 'B+': return 'bg-lime-500 text-white';
      case 'B': return 'bg-yellow-500 text-white';
      case 'C': return 'bg-orange-500 text-white';
      case 'D': return 'bg-red-400 text-white';
      case 'F': return 'bg-red-600 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 100) return 'text-emerald-600';
    if (score >= 80) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };
  
  const getStatusBadge = (status, isLocked) => {
    if (isLocked && status === 'approved') {
      return (
        <Badge className="bg-emerald-500 text-white">
          <CheckCircle className="h-3 w-3 mr-1" />
          Approved
        </Badge>
      );
    }
    
    switch (status) {
      case 'draft':
        return (
          <Badge variant="outline" className="border-md3-tertiary text-md3-tertiary">
            <FileText className="h-3 w-3 mr-1" />
            Draft
          </Badge>
        );
      case 'submitted':
        return (
          <Badge className="bg-blue-500 text-white">
            <Clock className="h-3 w-3 mr-1" />
            Pending Review
          </Badge>
        );
      case 'approved':
        return (
          <Badge className="bg-emerald-500 text-white">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-red-500 text-white">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </Badge>
        );
      case 'revision_requested':
        return (
          <Badge className="bg-amber-500 text-white">
            <RotateCcw className="h-3 w-3 mr-1" />
            Revision Needed
          </Badge>
        );
      default:
        if (isLocked) {
          return (
            <Badge variant="outline" className="border-md3-outline text-md3-on-surface-variant">
              <Lock className="h-3 w-3 mr-1" />
              Locked
            </Badge>
          );
        }
        return (
          <Badge variant="outline" className="border-md3-tertiary text-md3-tertiary">
            Draft
          </Badge>
        );
    }
  };
  
  // Check if current user can submit this summary for review
  const canSubmitForReview = (summary) => {
    // Status must be draft or revision_requested
    if (!['draft', 'revision_requested'].includes(summary.status)) return false;
    // Summary must not be locked
    if (summary.is_locked) return false;
    // Admin can always submit
    if (user?.access_level === "Admin") return true;
    // Otherwise, check if user is the approver (would need approver_id from assignment)
    return true; // For now, allow managers to submit - backend will validate
  };

  return (
    <Layout user={user} title="Performance Summary">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-md3-headline-medium text-md3-on-surface">Performance Summary</h1>
            <p className="text-md3-body-medium text-md3-on-surface-variant">
              Monthly performance calculation, reviews, and reports
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
              <SelectTrigger className="w-48 bg-md3-surface border-md3-outline rounded-md3-sm">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(d => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-36 bg-md3-surface border-md3-outline rounded-md3-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map(m => (
                  <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-24 bg-md3-surface border-md3-outline rounded-md3-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={handleGenerateAll}
              disabled={generating}
              className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
            >
              <Calculator className={`h-4 w-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Generating...' : 'Calculate All'}
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-md3-surface-container rounded-md3-full p-1">
            <TabsTrigger value="dashboard" className="rounded-md3-full data-[state=active]:bg-md3-secondary-container">
              <BarChart3 className="h-4 w-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="summaries" className="rounded-md3-full data-[state=active]:bg-md3-secondary-container">
              <Users className="h-4 w-4 mr-2" />
              Individual Summaries
            </TabsTrigger>
            {isHR && (
              <TabsTrigger value="reviews" className="rounded-md3-full data-[state=active]:bg-md3-secondary-container relative">
                <ClipboardCheck className="h-4 w-4 mr-2" />
                Pending Reviews
                {pendingCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {pendingCount}
                  </span>
                )}
              </TabsTrigger>
            )}
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6 mt-6">
            {dashboard && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-md3-body-small text-md3-on-surface-variant">Total Employees</p>
                          <p className="text-md3-headline-medium text-md3-on-surface">{dashboard.total_employees}</p>
                        </div>
                        <Users className="h-10 w-10 text-md3-primary" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-md3-body-small text-md3-on-surface-variant">Average Score</p>
                          <p className={`text-md3-headline-medium ${getScoreColor(dashboard.avg_score)}`}>
                            {dashboard.avg_score}%
                          </p>
                        </div>
                        <BarChart3 className="h-10 w-10 text-md3-secondary" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-md3-body-small text-md3-on-surface-variant">Top Performers</p>
                          <p className="text-md3-headline-medium text-emerald-600">{dashboard.top_performers?.length || 0}</p>
                        </div>
                        <TrendingUp className="h-10 w-10 text-emerald-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-md3-body-small text-md3-on-surface-variant">Needs Improvement</p>
                          <p className="text-md3-headline-medium text-red-600">{dashboard.needs_improvement?.length || 0}</p>
                        </div>
                        <TrendingDown className="h-10 w-10 text-red-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Grade Distribution & Department Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Grade Distribution */}
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardHeader>
                      <CardTitle className="text-md3-title-large text-md3-on-surface flex items-center gap-2">
                        <Award className="h-5 w-5" />
                        Grade Distribution
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {['A+', 'A', 'B+', 'B', 'C', 'D', 'F'].map(grade => {
                          const count = dashboard.grade_distribution?.[grade] || 0;
                          const percent = dashboard.total_employees > 0 
                            ? Math.round((count / dashboard.total_employees) * 100) 
                            : 0;
                          return (
                            <div key={grade} className="flex items-center gap-3">
                              <Badge className={`w-10 justify-center ${getGradeColor(grade)}`}>{grade}</Badge>
                              <div className="flex-1">
                                <Progress value={percent} className="h-2" />
                              </div>
                              <span className="text-md3-body-medium text-md3-on-surface-variant w-16 text-right">
                                {count} ({percent}%)
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Department Stats */}
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardHeader>
                      <CardTitle className="text-md3-title-large text-md3-on-surface flex items-center gap-2">
                        <Building2 className="h-5 w-5" />
                        Department Performance
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {dashboard.department_stats?.length > 0 ? (
                        <div className="space-y-3">
                          {dashboard.department_stats.map((dept, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-md3-surface-container rounded-md3-md">
                              <div>
                                <p className="text-md3-body-medium text-md3-on-surface font-medium">{dept.department}</p>
                                <p className="text-md3-body-small text-md3-on-surface-variant">{dept.employee_count} employees</p>
                              </div>
                              <span className={`text-md3-title-medium ${getScoreColor(dept.avg_score)}`}>
                                {dept.avg_score}%
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-md3-body-medium text-md3-on-surface-variant text-center py-4">
                          No department data available
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Top Performers & Needs Improvement */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Top Performers */}
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardHeader>
                      <CardTitle className="text-md3-title-large text-md3-on-surface flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-emerald-500" />
                        Top Performers (≥100%)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {dashboard.top_performers?.length > 0 ? (
                        <div className="space-y-2">
                          {dashboard.top_performers.map((emp, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-emerald-50 dark:bg-emerald-950/20 rounded-md3-md">
                              <span className="text-md3-body-medium text-md3-on-surface">{emp.employee_name}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-md3-title-small text-emerald-600">{emp.score}%</span>
                                <Badge className={getGradeColor(emp.grade)}>{emp.grade}</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-md3-body-medium text-md3-on-surface-variant text-center py-4">
                          No top performers yet
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Needs Improvement */}
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardHeader>
                      <CardTitle className="text-md3-title-large text-md3-on-surface flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-500" />
                        Needs Improvement (&lt;70%)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {dashboard.needs_improvement?.length > 0 ? (
                        <div className="space-y-2">
                          {dashboard.needs_improvement.map((emp, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-red-50 dark:bg-red-950/20 rounded-md3-md">
                              <span className="text-md3-body-medium text-md3-on-surface">{emp.employee_name}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-md3-title-small text-red-600">{emp.score}%</span>
                                <Badge className={getGradeColor(emp.grade)}>{emp.grade}</Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-md3-body-medium text-md3-on-surface-variant text-center py-4">
                          No employees need improvement
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Individual Summaries Tab */}
          <TabsContent value="summaries" className="space-y-4 mt-6">
            <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
              <CardHeader>
                <CardTitle className="text-md3-title-large text-md3-on-surface">
                  {months.find(m => m.value === selectedMonth)?.label} {selectedYear} Summaries
                </CardTitle>
                <CardDescription className="text-md3-body-small text-md3-on-surface-variant">
                  Individual performance summaries for the period
                </CardDescription>
              </CardHeader>
              <CardContent>
                {summaries.length === 0 ? (
                  <div className="text-center py-8 text-md3-body-medium text-md3-on-surface-variant">
                    <Calculator className="h-12 w-12 mx-auto mb-3 text-md3-outline" />
                    <p>No summaries generated for this period.</p>
                    <p className="text-md3-body-small mt-1">Click &quot;Calculate All&quot; to generate summaries.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-md3-outline-variant hover:bg-transparent">
                        <TableHead className="text-md3-label-large text-md3-on-surface-variant">Employee</TableHead>
                        <TableHead className="text-md3-label-large text-md3-on-surface-variant">Type</TableHead>
                        <TableHead className="text-md3-label-large text-md3-on-surface-variant">KRA Type</TableHead>
                        <TableHead className="text-md3-label-large text-md3-on-surface-variant">Earned / Total</TableHead>
                        <TableHead className="text-md3-label-large text-md3-on-surface-variant">Score</TableHead>
                        <TableHead className="text-md3-label-large text-md3-on-surface-variant">Grade</TableHead>
                        <TableHead className="text-md3-label-large text-md3-on-surface-variant">Status</TableHead>
                        <TableHead className="text-md3-label-large text-md3-on-surface-variant text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {summaries.map((summary) => (
                        <TableRow key={summary.id} className="border-md3-outline-variant">
                          <TableCell className="text-md3-body-medium text-md3-on-surface font-medium">
                            {summary.employee_name}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              summary.employee_type === 'intern' 
                                ? 'border-md3-tertiary text-md3-tertiary' 
                                : 'border-md3-primary text-md3-primary'
                            }>
                              {summary.employee_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-md3-body-medium text-md3-on-surface-variant">
                            {summary.kra_type_name}
                          </TableCell>
                          <TableCell className="text-md3-body-medium text-md3-on-surface">
                            {summary.total_earned_weightage}% / {summary.total_weightage}%
                          </TableCell>
                          <TableCell className={`text-md3-title-medium ${getScoreColor(summary.overall_score)}`}>
                            {summary.overall_score}%
                          </TableCell>
                          <TableCell>
                            <Badge className={getGradeColor(summary.performance_grade)}>
                              {summary.performance_grade}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(summary.status, summary.is_locked)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => openDetailDialog(summary.id)}
                                className="h-8 w-8 p-0 text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {canSubmitForReview(summary) && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => openSubmitDialog(summary)}
                                  className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-100 rounded-md3-full"
                                  title="Submit for Review"
                                >
                                  <Send className="h-4 w-4" />
                                </Button>
                              )}
                              {!summary.is_locked && summary.status !== 'submitted' && summary.status !== 'approved' && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleLockToggle(summary.id, summary.is_locked)}
                                  className="h-8 w-8 p-0 text-md3-outline hover:bg-md3-surface-container rounded-md3-full"
                                  title="Lock"
                                >
                                  <Lock className="h-4 w-4" />
                                </Button>
                              )}
                              {summary.is_locked && summary.status !== 'approved' && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => handleLockToggle(summary.id, summary.is_locked)}
                                  className="h-8 w-8 p-0 text-md3-tertiary hover:bg-md3-tertiary-container rounded-md3-full"
                                  title="Unlock"
                                >
                                  <Unlock className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending Reviews Tab (HR Only) */}
          {isHR && (
            <TabsContent value="reviews" className="space-y-4 mt-6">
              <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                <CardHeader>
                  <CardTitle className="text-md3-title-large text-md3-on-surface flex items-center gap-2">
                    <ClipboardCheck className="h-5 w-5" />
                    Pending Performance Reviews
                  </CardTitle>
                  <CardDescription className="text-md3-body-small text-md3-on-surface-variant">
                    Reviews awaiting HR approval for {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {pendingReviews.length === 0 ? (
                    <div className="text-center py-8 text-md3-body-medium text-md3-on-surface-variant">
                      <CheckCircle className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
                      <p>No pending reviews for this period.</p>
                      <p className="text-md3-body-small mt-1">All reviews have been processed.</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-md3-outline-variant hover:bg-transparent">
                          <TableHead className="text-md3-label-large text-md3-on-surface-variant">Employee</TableHead>
                          <TableHead className="text-md3-label-large text-md3-on-surface-variant">KRA Type</TableHead>
                          <TableHead className="text-md3-label-large text-md3-on-surface-variant">Score</TableHead>
                          <TableHead className="text-md3-label-large text-md3-on-surface-variant">Grade</TableHead>
                          <TableHead className="text-md3-label-large text-md3-on-surface-variant">Reviewer</TableHead>
                          <TableHead className="text-md3-label-large text-md3-on-surface-variant">Submitted</TableHead>
                          <TableHead className="text-md3-label-large text-md3-on-surface-variant text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pendingReviews.map((review) => (
                          <TableRow key={review.id} className="border-md3-outline-variant">
                            <TableCell className="text-md3-body-medium text-md3-on-surface font-medium">
                              {review.employee_name}
                            </TableCell>
                            <TableCell className="text-md3-body-medium text-md3-on-surface-variant">
                              {review.kra_type_name}
                            </TableCell>
                            <TableCell className={`text-md3-title-medium ${getScoreColor(review.overall_score)}`}>
                              {review.overall_score}%
                            </TableCell>
                            <TableCell>
                              <Badge className={getGradeColor(review.performance_grade)}>
                                {review.performance_grade}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-md3-body-medium text-md3-on-surface-variant">
                              {review.reviewer_name}
                            </TableCell>
                            <TableCell className="text-md3-body-small text-md3-on-surface-variant">
                              {review.submitted_at ? new Date(review.submitted_at).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button 
                                size="sm"
                                onClick={() => openReviewDialog(review)}
                                className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Review
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => setDetailDialog({ ...detailDialog, open })}>
        <DialogContent className="bg-md3-surface-container-high rounded-md3-xl max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small text-md3-on-surface">
              Performance Summary Details
            </DialogTitle>
            <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
              {detailDialog.summary?.employee_name} - {months.find(m => m.value === detailDialog.summary?.period_month)?.label} {detailDialog.summary?.period_year}
            </DialogDescription>
          </DialogHeader>
          
          {detailDialog.summary && (
            <div className="space-y-4">
              {/* Summary Card */}
              <div className="p-4 bg-md3-surface-container rounded-md3-lg">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Overall Score</p>
                    <p className={`text-md3-headline-medium ${getScoreColor(detailDialog.summary.overall_score)}`}>
                      {detailDialog.summary.overall_score}%
                    </p>
                  </div>
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Grade</p>
                    <Badge className={`text-lg ${getGradeColor(detailDialog.summary.performance_grade)}`}>
                      {detailDialog.summary.performance_grade}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Status</p>
                    {getStatusBadge(detailDialog.summary.status, detailDialog.summary.is_locked)}
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <h4 className="text-md3-title-medium text-md3-on-surface mb-2">Line Item Breakdown</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Line Item</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Actual</TableHead>
                      <TableHead>Achievement</TableHead>
                      <TableHead>Earned</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detailDialog.summary.line_items_summary?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <div>
                            <span className="font-medium">{item.line_item_name}</span>
                            <span className="text-xs text-md3-on-surface-variant ml-2">({item.weightage}%)</span>
                          </div>
                        </TableCell>
                        <TableCell>{item.target_value} {item.measurement_unit}</TableCell>
                        <TableCell>{item.actual_value} {item.measurement_unit}</TableCell>
                        <TableCell>
                          <span className={getScoreColor(item.achievement_percent)}>
                            {item.achievement_percent}%
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{item.earned_weightage}%</Badge>
                          {item.slab_applied && (
                            <span className="text-xs text-md3-on-surface-variant block">{item.slab_applied}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Review Comments (if submitted/approved) */}
              {detailDialog.summary.review_comments && (
                <div className="p-4 bg-md3-surface-container rounded-md3-lg">
                  <h4 className="text-md3-title-small text-md3-on-surface mb-2">HR Comments</h4>
                  <p className="text-md3-body-medium text-md3-on-surface-variant">
                    {detailDialog.summary.review_comments}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDetailDialog({ open: false, summary: null })}
              className="border-md3-outline text-md3-primary rounded-md3-full"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit for Review Dialog */}
      <Dialog open={submitDialog.open} onOpenChange={(open) => setSubmitDialog({ ...submitDialog, open })}>
        <DialogContent className="bg-md3-surface-container-high rounded-md3-xl max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small text-md3-on-surface">
              Submit Performance Review
            </DialogTitle>
            <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
              Submit {submitDialog.summary?.employee_name}&apos;s performance for HR review
            </DialogDescription>
          </DialogHeader>
          
          {submitDialog.summary && (
            <div className="space-y-4">
              {/* Summary Overview */}
              <div className="p-4 bg-md3-surface-container rounded-md3-lg">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Score</p>
                    <p className={`text-md3-headline-small ${getScoreColor(submitDialog.summary.overall_score)}`}>
                      {submitDialog.summary.overall_score}%
                    </p>
                  </div>
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Grade</p>
                    <Badge className={getGradeColor(submitDialog.summary.performance_grade)}>
                      {submitDialog.summary.performance_grade}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Period</p>
                    <p className="text-md3-body-medium text-md3-on-surface">
                      {months.find(m => m.value === submitDialog.summary.period_month)?.label} {submitDialog.summary.period_year}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Review Comments */}
              <div className="space-y-2">
                <Label className="text-md3-label-large text-md3-on-surface">Review Comments</Label>
                <Textarea 
                  value={submitForm.review_comments}
                  onChange={(e) => setSubmitForm(prev => ({ ...prev, review_comments: e.target.value }))}
                  placeholder="Overall comments on employee's performance..."
                  className="bg-md3-surface border-md3-outline rounded-md3-sm min-h-[80px]"
                />
              </div>
              
              {/* Strengths */}
              <div className="space-y-2">
                <Label className="text-md3-label-large text-md3-on-surface">Strengths</Label>
                <div className="flex gap-2">
                  <Input 
                    value={newStrength}
                    onChange={(e) => setNewStrength(e.target.value)}
                    placeholder="Add a strength..."
                    className="bg-md3-surface border-md3-outline rounded-md3-sm"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addStrength())}
                  />
                  <Button 
                    type="button" 
                    onClick={addStrength}
                    variant="outline"
                    className="border-md3-outline rounded-md3-full"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {submitForm.strengths.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {submitForm.strengths.map((s, idx) => (
                      <Badge key={idx} variant="secondary" className="bg-emerald-100 text-emerald-800 pr-1">
                        {s}
                        <button onClick={() => removeStrength(idx)} className="ml-1 hover:text-red-600">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Areas for Improvement */}
              <div className="space-y-2">
                <Label className="text-md3-label-large text-md3-on-surface">Areas for Improvement</Label>
                <div className="flex gap-2">
                  <Input 
                    value={newImprovement}
                    onChange={(e) => setNewImprovement(e.target.value)}
                    placeholder="Add an area for improvement..."
                    className="bg-md3-surface border-md3-outline rounded-md3-sm"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addImprovement())}
                  />
                  <Button 
                    type="button" 
                    onClick={addImprovement}
                    variant="outline"
                    className="border-md3-outline rounded-md3-full"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {submitForm.areas_for_improvement.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {submitForm.areas_for_improvement.map((a, idx) => (
                      <Badge key={idx} variant="secondary" className="bg-amber-100 text-amber-800 pr-1">
                        {a}
                        <button onClick={() => removeImprovement(idx)} className="ml-1 hover:text-red-600">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Recommendations */}
              <div className="space-y-2">
                <Label className="text-md3-label-large text-md3-on-surface">Recommendations</Label>
                <Textarea 
                  value={submitForm.recommendations}
                  onChange={(e) => setSubmitForm(prev => ({ ...prev, recommendations: e.target.value }))}
                  placeholder="Training needs, role changes, special recognition..."
                  className="bg-md3-surface border-md3-outline rounded-md3-sm min-h-[60px]"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setSubmitDialog({ open: false, summary: null })}
              className="border-md3-outline text-md3-on-surface rounded-md3-full"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitForReview}
              disabled={submitting}
              className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
            >
              {submitting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit for Review
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* HR Review Dialog */}
      <Dialog open={reviewDialog.open} onOpenChange={(open) => setReviewDialog({ ...reviewDialog, open })}>
        <DialogContent className="bg-md3-surface-container-high rounded-md3-xl max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small text-md3-on-surface">
              Review Performance Summary
            </DialogTitle>
            <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
              {reviewDialog.review?.employee_name} - {months.find(m => m.value === reviewDialog.review?.period_month)?.label} {reviewDialog.review?.period_year}
            </DialogDescription>
          </DialogHeader>
          
          {reviewDialog.review && (
            <div className="space-y-4">
              {/* Performance Summary */}
              <div className="p-4 bg-md3-surface-container rounded-md3-lg">
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Score</p>
                    <p className={`text-md3-headline-small ${getScoreColor(reviewDialog.review.overall_score)}`}>
                      {reviewDialog.review.overall_score}%
                    </p>
                  </div>
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Grade</p>
                    <Badge className={getGradeColor(reviewDialog.review.performance_grade)}>
                      {reviewDialog.review.performance_grade}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Earned Weightage</p>
                    <p className="text-md3-headline-small text-md3-on-surface">
                      {reviewDialog.review.total_earned_weightage}%
                    </p>
                  </div>
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Reviewer</p>
                    <p className="text-md3-body-medium text-md3-on-surface">
                      {reviewDialog.review.reviewer_name}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Manager/Mentor Review */}
              <Card className="border-md3-outline">
                <CardHeader className="pb-2">
                  <CardTitle className="text-md3-title-medium text-md3-on-surface">
                    Manager/Mentor Review
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {reviewDialog.review.review_comments && (
                    <div>
                      <p className="text-md3-label-medium text-md3-on-surface-variant">Comments</p>
                      <p className="text-md3-body-medium text-md3-on-surface mt-1">
                        {reviewDialog.review.review_comments}
                      </p>
                    </div>
                  )}
                  
                  {reviewDialog.review.strengths?.length > 0 && (
                    <div>
                      <p className="text-md3-label-medium text-md3-on-surface-variant">Strengths</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {reviewDialog.review.strengths.map((s, idx) => (
                          <Badge key={idx} className="bg-emerald-100 text-emerald-800">{s}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {reviewDialog.review.areas_for_improvement?.length > 0 && (
                    <div>
                      <p className="text-md3-label-medium text-md3-on-surface-variant">Areas for Improvement</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {reviewDialog.review.areas_for_improvement.map((a, idx) => (
                          <Badge key={idx} className="bg-amber-100 text-amber-800">{a}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {reviewDialog.review.recommendations && (
                    <div>
                      <p className="text-md3-label-medium text-md3-on-surface-variant">Recommendations</p>
                      <p className="text-md3-body-medium text-md3-on-surface mt-1">
                        {reviewDialog.review.recommendations}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Line Items Summary (if available) */}
              {reviewDialog.review.summary?.line_items_summary?.length > 0 && (
                <div>
                  <h4 className="text-md3-title-medium text-md3-on-surface mb-2">Performance Breakdown</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Line Item</TableHead>
                        <TableHead>Target</TableHead>
                        <TableHead>Actual</TableHead>
                        <TableHead>Achievement</TableHead>
                        <TableHead>Earned</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reviewDialog.review.summary.line_items_summary.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <span className="font-medium">{item.line_item_name}</span>
                            <span className="text-xs text-md3-on-surface-variant ml-1">({item.weightage}%)</span>
                          </TableCell>
                          <TableCell>{item.target_value} {item.measurement_unit}</TableCell>
                          <TableCell>{item.actual_value} {item.measurement_unit}</TableCell>
                          <TableCell className={getScoreColor(item.achievement_percent)}>
                            {item.achievement_percent}%
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{item.earned_weightage}%</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              {/* HR Comments */}
              <div className="space-y-2">
                <Label className="text-md3-label-large text-md3-on-surface">HR Comments</Label>
                <Textarea 
                  value={hrComments}
                  onChange={(e) => setHrComments(e.target.value)}
                  placeholder="Add your comments (required for rejection or revision request)..."
                  className="bg-md3-surface border-md3-outline rounded-md3-sm min-h-[80px]"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            <Button 
              variant="outline" 
              onClick={() => setReviewDialog({ open: false, review: null })}
              className="border-md3-outline text-md3-on-surface rounded-md3-full"
            >
              Cancel
            </Button>
            <Button 
              variant="outline"
              onClick={handleRequestRevision}
              disabled={processing}
              className="border-amber-500 text-amber-600 hover:bg-amber-50 rounded-md3-full"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Request Revision
            </Button>
            <Button 
              variant="outline"
              onClick={handleReject}
              disabled={processing}
              className="border-red-500 text-red-600 hover:bg-red-50 rounded-md3-full"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Reject
            </Button>
            <Button 
              onClick={handleApprove}
              disabled={processing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md3-full"
            >
              {processing ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
